// Priority actions across buying + selling, plus monthly activity data.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type BuyerActionKind = "review_photos" | "pay_now" | "confirm_receipt";
export type SellerActionKind = "submit_photos" | "mark_shipped";
export type ActionKind = BuyerActionKind | SellerActionKind;
export type DetailKind = "order" | "transaction" | "checkout";

export type PriorityAction = {
  id: string;
  kind: ActionKind;
  side: "buying" | "selling";
  card_name: string;
  counterparty_username: string | null;
  deadline_at: string | null;
  detail_kind: DetailKind;
  detail_id: string;
  amount_cents: number | null;
};

export const getPriorityActions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    // Buyer's buy orders
    const buyOrdersRes = await supabase
      .from("buy_orders")
      .select("id, card_name")
      .eq("buyer_id", userId);
    const orderNameMap = new Map<string, string>(
      (buyOrdersRes.data ?? []).map((o) => [o.id as string, o.card_name as string]),
    );
    const buyOrderIds = Array.from(orderNameMap.keys());

    // 1. Offers with photos awaiting buyer review
    let pendingReview: Array<{
      id: string;
      buy_order_id: string;
      seller_id: string;
      price_cents: number;
      buyer_confirmation_deadline: string | null;
    }> = [];
    if (buyOrderIds.length) {
      const r = await supabase
        .from("offers")
        .select("id, buy_order_id, seller_id, price_cents, buyer_confirmation_deadline")
        .in("buy_order_id", buyOrderIds)
        .eq("status", "pending_buyer_confirmation")
        .order("buyer_confirmation_deadline", { ascending: true, nullsFirst: false })
        .limit(20);
      pendingReview = (r.data ?? []) as any;
    }

    // 2. Pending payment transactions (buyer)
    const pendingPayRes = await supabase
      .from("transactions")
      .select("id, buy_order_id, seller_id, amount_cents, shipping_cents, payment_deadline_at")
      .eq("buyer_id", userId)
      .eq("status", "pending_payment")
      .order("payment_deadline_at", { ascending: true, nullsFirst: false })
      .limit(20);

    // 3. Delivered — awaiting buyer confirmation
    const deliveredRes = await supabase
      .from("transactions")
      .select("id, buy_order_id, seller_id, amount_cents, delivered_at")
      .eq("buyer_id", userId)
      .eq("status", "delivered")
      .order("delivered_at", { ascending: true, nullsFirst: false })
      .limit(20);

    // 4. Seller — accepted offers awaiting photo submission
    const acceptedRes = await supabase
      .from("offers")
      .select("id, buy_order_id, price_cents, updated_at")
      .eq("seller_id", userId)
      .eq("status", "accepted")
      .order("updated_at", { ascending: true })
      .limit(20);

    // 5. Seller — payment confirmed, needs to ship
    const toShipRes = await supabase
      .from("transactions")
      .select("id, buy_order_id, buyer_id, amount_cents, created_at")
      .eq("seller_id", userId)
      .eq("status", "funds_held")
      .order("created_at", { ascending: true })
      .limit(20);


    // Enrich missing buy_order names
    const missingOrderIds = new Set<string>();
    const collect = (id: string | null | undefined) => {
      if (id && !orderNameMap.has(id)) missingOrderIds.add(id);
    };
    (pendingPayRes.data ?? []).forEach((t) => collect(t.buy_order_id));
    (deliveredRes.data ?? []).forEach((t) => collect(t.buy_order_id));
    (acceptedRes.data ?? []).forEach((o) => collect(o.buy_order_id));
    (toShipRes.data ?? []).forEach((t) => collect(t.buy_order_id));

    const acceptedOrderBuyerMap = new Map<string, string>();
    if (missingOrderIds.size) {
      const r = await supabase
        .from("buy_orders")
        .select("id, card_name, buyer_id")
        .in("id", Array.from(missingOrderIds));
      for (const row of r.data ?? []) {
        orderNameMap.set(row.id as string, row.card_name as string);
        acceptedOrderBuyerMap.set(row.id as string, row.buyer_id as string);
      }
    }

    // Counterparty usernames
    const counterpartyIds = new Set<string>();
    pendingReview.forEach((o) => counterpartyIds.add(o.seller_id));
    (pendingPayRes.data ?? []).forEach((t) => t.seller_id && counterpartyIds.add(t.seller_id));
    (deliveredRes.data ?? []).forEach((t) => t.seller_id && counterpartyIds.add(t.seller_id));
    (toShipRes.data ?? []).forEach((t) => t.buyer_id && counterpartyIds.add(t.buyer_id));
    (acceptedRes.data ?? []).forEach((o) => {
      const bid = acceptedOrderBuyerMap.get(o.buy_order_id ?? "");
      if (bid) counterpartyIds.add(bid);
    });
    counterpartyIds.delete(userId);

    const usernameMap = new Map<string, string>();
    if (counterpartyIds.size) {
      const r = await supabase
        .from("profiles")
        .select("id, username")
        .in("id", Array.from(counterpartyIds));
      for (const p of r.data ?? []) usernameMap.set(p.id as string, (p.username as string) ?? "");
    }

    const buying: PriorityAction[] = [];
    for (const o of pendingReview) {
      buying.push({
        id: `review-${o.id}`,
        kind: "review_photos",
        side: "buying",
        card_name: orderNameMap.get(o.buy_order_id) ?? "Card",
        counterparty_username: usernameMap.get(o.seller_id) ?? null,
        deadline_at: o.buyer_confirmation_deadline,
        detail_kind: "order", detail_id: o.buy_order_id,
        amount_cents: o.price_cents,
      });
    }
    for (const t of pendingPayRes.data ?? []) {
      buying.push({
        id: `pay-${t.id}`,
        kind: "pay_now",
        side: "buying",
        card_name: orderNameMap.get(t.buy_order_id ?? "") ?? "Card",
        counterparty_username: t.seller_id ? usernameMap.get(t.seller_id) ?? null : null,
        deadline_at: t.payment_deadline_at,
        detail_kind: "checkout", detail_id: t.id,
        amount_cents: (t.amount_cents ?? 0) + (t.shipping_cents ?? 0),
      });
    }
    for (const t of deliveredRes.data ?? []) {
      buying.push({
        id: `confirm-${t.id}`,
        kind: "confirm_receipt",
        side: "buying",
        card_name: orderNameMap.get(t.buy_order_id ?? "") ?? "Card",
        counterparty_username: t.seller_id ? usernameMap.get(t.seller_id) ?? null : null,
        deadline_at: t.delivered_at,

        detail_kind: "transaction", detail_id: t.id,
        amount_cents: t.amount_cents,
      });
    }

    const selling: PriorityAction[] = [];
    for (const o of acceptedRes.data ?? []) {
      const buyerId = acceptedOrderBuyerMap.get(o.buy_order_id ?? "") ?? null;
      selling.push({
        id: `photos-${o.id}`,
        kind: "submit_photos",
        side: "selling",
        card_name: orderNameMap.get(o.buy_order_id ?? "") ?? "Card",
        counterparty_username: buyerId ? usernameMap.get(buyerId) ?? null : null,
        deadline_at: o.updated_at,
        detail_kind: "order", detail_id: o.buy_order_id,
        amount_cents: o.price_cents,
      });
    }
    for (const t of toShipRes.data ?? []) {
      selling.push({
        id: `ship-${t.id}`,
        kind: "mark_shipped",
        side: "selling",
        card_name: orderNameMap.get(t.buy_order_id ?? "") ?? "Card",
        counterparty_username: t.buyer_id ? usernameMap.get(t.buyer_id) ?? null : null,
        deadline_at: t.created_at,
        detail_kind: "transaction", detail_id: t.id,
        amount_cents: t.amount_cents,
      });
    }

    const byUrgency = (a: PriorityAction, b: PriorityAction) => {
      const da = a.deadline_at ? new Date(a.deadline_at).getTime() : Infinity;
      const db = b.deadline_at ? new Date(b.deadline_at).getTime() : Infinity;
      return da - db;
    };
    buying.sort(byUrgency);
    selling.sort(byUrgency);

    return { buying, selling, total: buying.length + selling.length };
  });

export const getMonthlyActivity = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const now = new Date();
    const since = new Date(now.getFullYear(), now.getMonth() - 11, 1);

    const [buyingRes, sellingRes] = await Promise.all([
      supabase
        .from("transactions")
        .select("amount_cents, shipping_cents, created_at")
        .eq("buyer_id", userId)
        .gte("created_at", since.toISOString())
        .in("status", ["funds_held", "shipped", "delivered", "released"]),
      supabase
        .from("transactions")
        .select("amount_cents, platform_fee_cents, created_at")
        .eq("seller_id", userId)
        .gte("created_at", since.toISOString())
        .in("status", ["released"]),
    ]);

    const months: {
      key: string;
      label: string;
      buying_cents: number;
      selling_cents: number;
    }[] = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(since.getFullYear(), since.getMonth() + i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      months.push({
        key,
        label: d.toLocaleString("en-US", { month: "short" }),
        buying_cents: 0,
        selling_cents: 0,
      });
    }
    const idx = new Map(months.map((m, i) => [m.key, i]));
    for (const t of buyingRes.data ?? []) {
      const d = new Date(t.created_at as string);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const i = idx.get(key);
      if (i !== undefined)
        months[i].buying_cents +=
          (t.amount_cents ?? 0) + ((t as any).shipping_cents ?? 0);
    }
    for (const t of sellingRes.data ?? []) {
      const d = new Date(t.created_at as string);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const i = idx.get(key);
      if (i !== undefined)
        months[i].selling_cents +=
          (t.amount_cents ?? 0) - ((t as any).platform_fee_cents ?? 0);
    }
    return { months };
  });
